import { useEffect, useState } from 'react';

import type { FetchPackageFn, PackageCardData, ResolvedPackageCardData } from '../../../types';

/**
 * Batch-fetches package card data and keeps only successful responses.
 */
export const usePackageCardsData = (
  packageCards: PackageCardData[],
  onFetchPackage?: FetchPackageFn
) => {
  const [data, setData] = useState<ResolvedPackageCardData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!onFetchPackage || packageCards.length === 0) {
      setData([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const validCards = packageCards.filter((c) => c.listingId && c.packageId);

    Promise.all(
      validCards.map((c) =>
        onFetchPackage(c.listingId as string, c.packageId as string)
          .then((packageDetails) => ({
            ...c,
            ...packageDetails,
          }))
          .catch(() => null)
      )
    ).then((results) => {
      if (cancelled) return;

      setData(results.filter(Boolean) as ResolvedPackageCardData[]);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [onFetchPackage, packageCards]);

  return { data, isLoading };
};
